/**
 * Frontier Regulation Tracker — global jurisdictional status of AI rules
 * applicable to frontier-model providers + deployers.
 */
import { useEffect, useMemo, useState } from 'react'
import { Scale, ExternalLink, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'

interface Reg {
  id: string
  jurisdiction: string
  country: string
  title: string
  status: string
  effective_from: string
  applies_to: string[]
  summary: string
  url: string
}

const FLAG: Record<string, string> = {
  EU: '🇪🇺', GB: '🇬🇧', US: '🇺🇸', CN: '🇨🇳', SG: '🇸🇬', JP: '🇯🇵',
  KR: '🇰🇷', Global: '🌐', BE: '🇧🇪',
}

function statusTone(s: string): string {
  if (s.includes('in force'))     return 'bg-emerald/20 text-emerald border-emerald/40'
  if (s.includes('voluntary'))    return 'bg-blue/15 text-blue-light border-blue/40'
  if (s.includes('open for'))     return 'bg-amber/15 text-amber-light border-amber/40'
  if (s.includes('rescinded'))    return 'bg-rose/15 text-rose-light border-rose/40'
  return 'bg-surface-raised text-text-muted border-border'
}

export default function FrontierRegulationPage() {
  const [regs, setRegs] = useState<Reg[]>([])
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    api.get('/api/frontier/regulations')
      .then(r => setRegs(r.data?.regulations || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const inForce = regs.filter(r => r.status.includes('in force')).length

  // Cumulative regs effective by year — shows the regulatory wave building
  const byYearCumulative = useMemo(() => {
    if (regs.length === 0) return []
    const years = regs.map(r => parseInt((r.effective_from || '0000').slice(0, 4), 10)).filter(y => y > 0)
    if (years.length === 0) return []
    const minY = Math.min(...years)
    const maxY = Math.max(...years)
    const out: { t: string; v: number }[] = []
    let cumulative = 0
    for (let y = minY; y <= maxY; y++) {
      cumulative += regs.filter(r => parseInt(r.effective_from.slice(0, 4), 10) === y).length
      out.push({ t: String(y), v: cumulative })
    }
    return out
  }, [regs])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Scale size={18} className="text-blue-light" /> FRONTIER REGULATION TRACKER
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          global · jurisdictional · status by date
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total tracked"        value={regs.length}                                tone="blue"
              spark={byYearCumulative} sparkTone="blue" />
        <Tile label="In force"             value={inForce}                                    tone="emerald" />
        <Tile label="Voluntary frameworks" value={regs.filter(r => r.status.includes('voluntary')).length} tone="amber" />
        <Tile label="Jurisdictions"        value={new Set(regs.map(r => r.jurisdiction)).size} tone="rose" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {regs.map(r => (
          <div key={r.id} className="card p-4">
            <div className="flex items-start gap-2 mb-2">
              <span className="text-2xl shrink-0">{FLAG[r.country] || '🌐'}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-text-muted">{r.jurisdiction}</div>
                <h3 className="text-sm font-semibold text-text-primary leading-snug">{r.title}</h3>
              </div>
              <span className={clsx('badge border shrink-0', statusTone(r.status))}>{r.status}</span>
            </div>
            <div className="text-[10px] text-text-muted font-mono mt-1">
              Effective from {r.effective_from} · applies to {r.applies_to.join(', ')}
            </div>
            <p className="text-xs text-text-secondary mt-2 leading-relaxed">{r.summary}</p>
            <a href={r.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
              Reference <ExternalLink size={10} />
            </a>
          </div>
        ))}
      </div>
    </div>
  )
}

function Tile({ label, value, tone, spark, sparkTone }: {
  label: string; value: number;
  tone: 'rose' | 'emerald' | 'amber' | 'blue';
  spark?: { t: string; v: number }[];
  sparkTone?: 'rose' | 'emerald' | 'amber' | 'blue';
}) {
  const c = tone === 'rose' ? 'text-rose' : tone === 'emerald' ? 'text-emerald' : tone === 'amber' ? 'text-amber-light' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="flex items-baseline gap-2">
        <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
        {spark && spark.length >= 2 && (
          <Sparkline points={spark} tone={sparkTone || 'auto'} width={50} height={14} className="ml-auto" />
        )}
      </div>
      <div className={clsx('text-2xl font-black font-mono mt-1', c)}>{value.toLocaleString()}</div>
    </div>
  )
}
